import { useState, useRef, useEffect } from "react";
import { logoutUser } from "../../utils/AuthUtils";
import { Link, useNavigate } from "react-router-dom";
import * as utils from "../../utils/utils";
import { useStore } from "../user/UserContext";
import Icon from "../Icon/Icon";
import { useTheme } from "../Theme/ThemeContext";

function generateAvatarInitials(name: string, surname: string): string {
  return `${name.charAt(0).toUpperCase()}${surname ? surname.charAt(0).toUpperCase() : ""}`;
}

function UserDropdown() {
  const { store, setStore } = useStore();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const wrapperRef = useRef<HTMLDivElement>(null);

  function logout() {
    // Routed through the helper so a shadow session is ended on the way out.
    // Logging out mid-shadow used to leave the admin's spare token and the
    // shadowed user behind in storage.
    logoutUser(store, setStore);
    navigate("/login");
  }

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdownOpen) return;
      if (wrapperRef.current && !wrapperRef.current.contains(target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", clickHandler);
    return () => document.removeEventListener("mousedown", clickHandler);
  }, [dropdownOpen]);

  // close on Escape key
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  return (
    <div ref={wrapperRef} className="2xsm:gap-7 flex items-center gap-3 pr-4">
      <div className="relative">
        <Link
          onClick={toggleDropdown}
          className="flex items-center gap-3 opacity-90 transition-opacity hover:opacity-100"
          to="#"
        >
          <div className="hidden flex-col text-right md:flex">
            <span
              className={`block text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}
            >
              {utils.user(store)?.name}
            </span>
            <span
              className={`block text-[11px] ${isDark ? "text-primary-light/80" : "text-gray-500"}`}
            >
              {utils.titleCase(utils.user(store)?.role)}
            </span>
          </div>
          {utils.profilePicture(store) ? (
            <img
              className="h-9 w-9 rounded-full ring-2 ring-primary/40"
              src={utils.profilePicture(store)}
              alt="User Profile"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-white ring-2 ring-primary/40">
              {generateAvatarInitials(utils.user(store)?.name, utils.user(store)?.surname)}
            </div>
          )}
          <svg
            className={`opacity-60 ${isDark ? "fill-white" : "fill-gray-600"}`}
            width="10"
            height="7"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.410765 0.910734C0.736202 0.585297 1.26384 0.585297 1.58928 0.910734L6.00002 5.32148L10.4108 0.910734C10.7362 0.585297 11.2638 0.585297 11.5893 0.910734C11.9147 1.23617 11.9147 1.76381 11.5893 2.08924L6.58928 7.08924C6.26384 7.41468 5.7362 7.41468 5.41077 7.08924L0.410765 2.08924C0.0853277 1.76381 0.0853277 1.23617 0.410765 0.910734Z"
            />
          </svg>
        </Link>

        {dropdownOpen && (
          <div className="absolute right-0 z-50 mt-3 w-72 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-2xl">
            {/* Header */}
            <div className="bg-primary-dark px-4 py-4">
              <div className="flex items-center gap-3">
                {utils.profilePicture(store) ? (
                  <img
                    className="h-11 w-11 rounded-full ring-2 ring-white/30"
                    src={utils.profilePicture(store)}
                    alt="User Profile"
                  />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-sm font-bold text-white ring-2 ring-white/20">
                    {generateAvatarInitials(utils.user(store)?.name, utils.user(store)?.surname)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">
                    {utils.user(store)?.name} {utils.user(store)?.surname}
                  </p>
                  <p className="truncate text-[11px] text-primary-light/70">
                    {utils.user(store)?.email}
                  </p>
                  <span className="mt-1 inline-block rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-medium text-primary-light">
                    {utils.titleCase(utils.user(store)?.role)}
                  </span>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="p-2">
              <Link
                to="/my-profile"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
                  <Icon name="user" className="text-gray-500" />
                </span>
                <span className="font-medium">My Profile</span>
              </Link>
              <Link
                to="/support"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
                  <Icon name="fa-circle-exclamation" className="text-gray-500" />
                </span>
                <span className="font-medium">Help & Support</span>
              </Link>
              <Link
                to="/settings"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
                  <Icon name="fa-gear" className="text-gray-500" />
                </span>
                <span className="font-medium">Settings</span>
              </Link>

              {!store?.impersonatingUser && (
                <>
                  <div className="my-2 border-t border-gray-100" />
                  <button
                    onClick={logout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50">
                      <Icon name="logout" className="text-red-500" />
                    </span>
                    <span className="font-medium">Sign Out</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDropdown;
